import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface RecommendedProduct {
  id: string;
  title: string;
  type: "video" | "kit";
  price: number;
  category: string | null;
  sales_count: number;
  thumbnail_url: string | null;
  provider_name?: string;
  provider_id: string;
}

const PURPOSE_LABELS: Record<string, string> = {
  hobby: "趣味として学びたい",
  skill: "手に職をつけたい",
  career: "将来の仕事につなげたい",
  return: "復職の準備をしたい",
  change: "転職・キャリアチェンジ",
  business: "独立・副業の参考に",
  succession: "家業・事業承継",
  research: "情報収集したい",
  other: "その他",
};

// Purpose to preferred category keywords mapping
const PURPOSE_CATEGORIES: Record<string, string[]> = {
  hobby: ["和菓子", "書道", "陶芸", "工芸", "料理"],
  skill: ["大工", "木工", "陶芸", "理容", "美容"],
  career: ["理容", "美容", "大工", "木工"],
  return: ["理容", "美容", "和菓子"],
  change: ["理容", "美容", "木工"],
  business: ["和服", "着物", "大工", "木工"],
  succession: ["和服", "着物", "しみ抜き"],
  research: [],
  other: [],
};

export default function RecommendedCourses() {
  const { currentUser } = useApp();
  const [products, setProducts] = useState<RecommendedProduct[]>([]);
  const [purpose, setPurpose] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.id) { setLoading(false); return; }

      // Get user's purpose
      const { data: profile } = await supabase
        .from("profiles")
        .select("purpose")
        .eq("id", currentUser.id)
        .maybeSingle();

      const userPurpose = profile?.purpose || "";
      setPurpose(userPurpose);

      // Get already-purchased product IDs
      const { data: purchases } = await supabase
        .from("purchases")
        .select("product_id")
        .eq("buyer_id", currentUser.id)
        .eq("status", "completed");
      const purchasedIds = (purchases || []).map(p => p.product_id);

      // Build query for recommendations
      let query = supabase
        .from("products")
        .select("id, title, type, price, category, sales_count, thumbnail_url, provider_id")
        .eq("visibility", "public")
        .eq("status", "public")
        .order("sales_count", { ascending: false })
        .limit(20);

      const { data: allProducts } = await query;
      if (!allProducts) { setLoading(false); return; }

      // Filter out already purchased
      let filtered = allProducts.filter(p => !purchasedIds.includes(p.id));

      // Apply purpose-based scoring/filtering
      const preferredCategories = PURPOSE_CATEGORIES[userPurpose] || [];
      if (preferredCategories.length > 0) {
        const withCategory = filtered.filter(p =>
          p.category && preferredCategories.some(cat => p.category?.includes(cat))
        );
        if (withCategory.length >= 3) {
          filtered = withCategory;
        }
      }

      // Take top 6
      const top6 = filtered.slice(0, 6);

      // Get provider names
      const providerIds = [...new Set(top6.map(p => p.provider_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, name, service_name")
        .in("id", providerIds);
      const profileMap = Object.fromEntries(
        (profiles || []).map(p => [p.id, p.service_name || p.name || "事業者"])
      );

      setProducts(top6.map(p => ({ ...p, provider_name: profileMap[p.provider_id] })));
      setLoading(false);
    };
    load();
  }, [currentUser?.id]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-stone-200 rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  if (products.length === 0) return null;

  const getThumbnail = (p: RecommendedProduct) =>
    p.thumbnail_url ||
    `https://public.readdy.ai/ai/img_res/31b6fed883469f98c7cacfd1236d66eb.jpg$%7Bp.category%20%3F%20%20%20%20%20p.category%20:%20%7D&width=300&height=200&seq=rec-${p.id.slice(0,8)}&orientation=landscape`;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-sparkling-2-line text-gold text-base" />
            <h3 className="font-serif text-deep-green text-lg">あなたへのおすすめ講座</h3>
          </div>
          {purpose && PURPOSE_LABELS[purpose] && (
            <p className="text-xs text-charcoal/50 font-sans">
              「{PURPOSE_LABELS[purpose]}」の目的に基づいてピックアップしました
            </p>
          )}
        </div>
        <Link to="/categories" className="text-xs text-teal font-sans hover:text-deep-green transition-colors cursor-pointer whitespace-nowrap">
          もっと見る →
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(p => (
          <Link
            key={p.id}
            to={`/${p.type === "video" ? "courses" : "materials"}/${p.id}`}
            className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:-translate-y-1 transition-all duration-300 cursor-pointer block group"
          >
            <div className="w-full h-36 overflow-hidden relative">
              <img
                src={getThumbnail(p)}
                alt={p.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-deep-green/60 to-transparent" />
              <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-sans ${p.type === "video" ? "bg-teal/90 text-white" : "bg-gold/90 text-white"}`}>
                {p.type === "video" ? "動画" : "キット"}
              </span>
            </div>
            <div className="p-4">
              <p className="text-xs text-charcoal/40 font-sans mb-1">{p.provider_name}</p>
              <p className="font-sans text-sm text-charcoal font-medium leading-snug line-clamp-2">{p.title}</p>
              <div className="flex items-center justify-between mt-3">
                <p className="font-serif text-deep-green text-base">
                  {p.price === 0 ? <span className="text-teal text-xs">無料</span> : `¥${p.price.toLocaleString()}`}
                </p>
                {p.sales_count > 0 && (
                  <p className="text-xs text-charcoal/40 font-sans">{p.sales_count}件販売</p>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {!purpose && (
        <div className="mt-4 p-4 bg-ivory rounded-2xl border border-stone-100 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-map-pin-user-line text-teal text-xl" />
            </div>
            <div>
              <p className="text-sm font-sans font-medium text-charcoal">利用目的を設定するともっとぴったりな講座が表示されます</p>
              <p className="text-xs text-charcoal/50 font-sans">設定タブのプロフィール設定から選べます</p>
            </div>
          </div>
          <Link
            to="/mypage"
            className="text-xs text-teal font-sans whitespace-nowrap cursor-pointer hover:text-deep-green transition-colors"
          >
            設定する →
          </Link>
        </div>
      )}
    </div>
  );
}
